// The composer — a full-screen sheet for writing, ported from the handoff's ComposerScreen.
//
// Waking and Dream are the two modes, matching iOS. The date header, the mode switch, and the
// "today I visited / today I dreamt" prompt are all from the design handoff; what's new here is that
// it actually saves: encrypt with the session key, push to /api/entries/sync, and the iPhone sees it.
//
// Draft safety: unfinished writing is saved to the server as a real entry marked is_draft, so a
// closed tab, a dead battery or a different device all find it again. It appears in the journal's
// Drafts section rather than being handed back the next time someone starts something new — a blank
// page has to actually be blank, or "new entry" becomes a game of dismissing an old thought first.
// Saving is what publishes it: the same row, with the draft flag cleared.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { PageScanSheet } from '../components/PageScanSheet';
import { useEntries } from '../entries/EntriesProvider';
import {
  derivedDreamType,
  deriveSymbols,
  emptyDreamDetails,
  hasStructuredDreamData,
  suggestTitle,
  type DreamDetails,
  type DreamType,
  type EntryDraft,
} from '../api/entries';
import { extractDreamDetails, longEnoughToExtract, mergeExtracted, markOreiaFilled, shouldSilentBackfill } from '../api/dream-extract';
import { fetchEntitlement } from '../api/billing';
import { ApiError } from '../api/client';
import { Notice } from '../components/ui';
import { IconClose, IconImage, IconMic, IconSliders, IconSpark } from '../components/Icons';
import { AnalysisMenuButton, EntryAnalysisSheet } from './EntryAnalysis';
import type { AnalysisKind } from '../api/analysis';
import {
  DreamCompanionPanel,
  DreamDetailsPage,
  DreamLucidityPage,
  DreamPageTabs,
  DreamSymbolsPage,
  type DreamPage,
} from './DreamForm';
import { DreamVisionFlow } from './DreamVisionFlow';
import { CoverChooser } from './CoverChooser';
import { isTinted, readerVars, ReaderSettingsSheet, useReaderSettings } from './ReaderSettings';
import { canDictate, VoiceDictationSheet } from './VoiceDictation';
import {
  EntryPhotoColumn,
  EntryPhotoStrip,
  PhotoPickerButton,
  PhotoViewer,
  useEntryPhotos,
} from './EntryPhotos';
import { CompanionPanel } from './CompanionPanel';
import { entryPreview } from '../entries/preview';
import { resolveCitation } from '../api/citations';
import { longDate } from '../format/dates';
import { ConfirmDialog, type ConfirmSpec } from '../components/SettingsKit';
import { useJournals } from '../journals/JournalsProvider';
import { IconFolder } from '../components/Icons';

type Mode = 'waking' | 'dream';

/**
 * How long the typing has to stop before an unfinished entry is written to the server.
 *
 * Long enough that a normal sentence is one save rather than twenty, short enough that walking away
 * mid-thought still keeps it. The exit path saves immediately regardless, so this only decides how
 * much a crash could cost — a few seconds of writing.
 */
const DRAFT_DEBOUNCE_MS = 2500;

/**
 * Below this, an abandoned composer is left alone.
 *
 * Opening the composer and typing one character then leaving is not a draft, it is a misfire, and
 * turning it into a row would fill the Drafts section with things nobody meant to write. A title on
 * its own always counts, since typing one is deliberate.
 */
const DRAFT_MIN_CHARS = 12;

/**
 * The photo column's own collapsed state, kept apart from the dream panel's `oreia.composer
 * .companion`. Hiding the dream's fields and hiding the pictures are different wishes, and one key
 * would have each mode answering for the other.
 */
const PHOTO_PANEL_KEY = 'oreia.composer.photos';

/**
 * The id a cover is derived from while the entry has none.
 *
 * `entryPreview` hashes the id to pick an aura or a gradient, so it needs *an* id even for an entry
 * that has never been saved. A constant rather than a fresh uuid: the picture then holds still
 * while the entry is being written, and only changes when the writer changes something — the mode,
 * or the symbols — which is the one reason it should ever change.
 */
const UNSAVED_PREVIEW_ID = 'oreia.composer.unsaved';

/**
 * Where the dream's structured fields stop being tabs and become a facing page beside the story.
 *
 * Read in JavaScript rather than expressed only in CSS because the two presentations are different
 * content, not one markup laid out twice: rendering the tabbed pages and the stacked column together
 * and hiding one would put every control on the page twice, which a screen reader and a Tab key would
 * both find. The width bands themselves live in shell.css and this number has to match the one there.
 *
 * 1024 rather than 1180: the spread used to be a fixed 1068px, so a laptop window at 1024 fell back
 * to the tabs and the pictures and dream fields both went behind them. The spread gives ground now
 * (see --comp-w in oreia.css), so the panel reaches the width people actually have a window at.
 */
const COMPANION_WIDTH = '(min-width: 1024px)';

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(query).matches === true,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

const PROMPTS: Record<Mode, string> = {
  waking: 'today I visited',
  dream: 'today I dreamt',
};

// The three iOS can read. "Recurring", "Prophetic" and "Healing" used to be offered here as dream
// types, which iOS's `DreamType(rawValue:)` rejects — those dreams arrived on the phone with no type
// at all. They are not lost: iOS models the same ideas inside the dream payload, so recurring is the
// toggle on the Details page and the other two are "Precognitive" and "Healing" under nature of the
// dream, where the server's extraction already looks for them.
const DREAM_TYPES: { value: DreamType; label: string }[] = [
  { value: 'normal', label: 'Dream' },
  { value: 'lucid', label: 'Lucid' },
  { value: 'nightmare', label: 'Nightmare' },
];

export function ComposerScreen() {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showScan,setShowScan]=useState(false);
  const { save, byId, remove } = useEntries();

  const existing = entryId ? byId(entryId) : undefined;
  const isEditing = Boolean(existing);

  /**
   * An opening draft handed over by another screen — currently a conversation woven into prose.
   *
   * It lands in the real composer rather than a bespoke preview sheet so the text arrives editable,
   * with the same title, symbols, dream fields and save path as anything typed by hand. Nothing is
   * saved until the user saves it, so a weave they dislike costs them one Cancel.
   */
  const seed = (location.state as { seed?: { title?: string; body?: string; kind?: Mode } } | null)
    ?.seed;

  const [mode, setMode] = useState<Mode>(existing?.kind ?? seed?.kind ?? 'waking');
  const [title, setTitle] = useState(existing?.title ?? seed?.title ?? '');
  const [body, setBody] = useState(existing?.body ?? seed?.body ?? '');
  const [dreamType, setDreamType] = useState<DreamType>(existing?.dreamType ?? 'normal');
  const [symbolsText, setSymbolsText] = useState((existing?.symbols ?? []).join(', '));
  const [showMeta, setShowMeta] = useState(false);
  const [page, setPage] = useState<DreamPage>('story');
  const [reader, updateReader] = useReaderSettings();
  const [showVoice, setShowVoice] = useState(false);
  const [showImagine, setShowImagine] = useState(false);
  const [showCover, setShowCover] = useState(false);
  /** The lens currently being read through, or null when no analysis sheet is open. */
  const [analysisKind, setAnalysisKind] = useState<AnalysisKind | null>(null);
  const [extracting, setExtracting] = useState(false);
  /** What the last extraction did, said in the panel rather than as an error banner. */
  const [extractNote, setExtractNote] = useState<string | null>(null);
  /**
   * Whether the silent pass after a save is worth making at all.
   *
   * Extraction is premium. Firing it for a free account means a 402 on every dream saved — a wasted
   * round trip and a console error for a feature that was never offered. Unknown counts as no: a
   * lookup that failed is not evidence of an entitlement, and the cost of guessing wrong is a
   * request nobody asked for.
   *
   * The button is a different case and does not consult this. Someone who presses it has asked, and
   * the honest answer to an ask is what the endpoint says, including that it is part of Premium.
   */
  const [premium, setPremium] = useState(false);
  /**
   * The row this composer owns once autosave has written something.
   *
   * A new entry has no id until it is first saved. Remembering the one autosave created is what
   * keeps a session from leaving a trail of near-identical drafts behind it — every later save,
   * including the publish, updates this row instead of making another. It is also what lets photos
   * attach before the entry is published, rather than waiting in the tab.
   */
  const [draftId, setDraftId] = useState<string | null>(null);
  const entryRowId = existing?.id ?? draftId;
  const photos = useEntryPhotos(entryRowId);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  // Backdating, which iOS offers from the same sheet. An entry written up days later should sit in
  // the journal on the night it happened, not the evening it was typed.
  const [entryDate, setEntryDate] = useState<Date>(() => existing?.createdAt ?? new Date());
  const [details, setDetails] = useState<DreamDetails>(
    () => existing?.dreamDetails ?? emptyDreamDetails(),
  );

  // Merges one field at a time so the untouched ones — including any a newer iOS added and this build
  // does not know about — survive an edit made here.
  const patchDetails = useCallback((patch: Partial<DreamDetails>) => {
    setDetails((current) => ({ ...current, ...patch }));
  }, []);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  /** Set once the entry is published, so the exit path can't quietly turn it back into a draft. */
  const published = useRef(false);

  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const isDream = mode === 'dream';

  // The title field grows to fit what is in it. Measured rather than guessed, because the height of
  // a line depends on the reader's own type settings, and it runs before paint so a two-line title
  // never appears clipped for a frame on the way in.
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [title, reader]);

  /** A quote Oreia cited, handed over in router state so private text stays out of the URL. */
  const highlight = (location.state as { highlight?: { quote: string; start: number; end: number } } | null)
    ?.highlight;
  const [highlightState, setHighlightState] = useState<'idle' | 'found' | 'drifted'>('idle');
  const highlightDone = useRef(false);

  // Wide enough for the companion panel, which replaces the tabs rather than joining them. The story
  // is then the only thing the card holds, whatever page was last selected on a narrower window.
  const isWide = useMediaQuery(COMPANION_WIDTH);
  const hasCompanion = isDream && isWide;
  const activePage: DreamPage = isDream && !hasCompanion ? page : 'story';

  const closeTo = (location.state as { from?: string } | null)?.from ?? '/journal';

  /**
   * "Write here" opened this from a journal, so the entry is filed there when it is published.
   *
   * The journal is named in the URL rather than in router state: someone who reloads mid-sentence
   * should still be writing into the same notebook. Only for a new entry — reopening an entry that
   * already exists is editing it, not filing it somewhere.
   */
  const { byId: journalById, addEntry: addToJournal } = useJournals();
  const destination = !entryId
    ? journalById(new URLSearchParams(location.search).get('journal') ?? '')
    : undefined;

  useEffect(() => {
    bodyRef.current?.focus();
  }, []);

  // Only once a dream is being written, rather than on every composer open: a waking entry has no
  // extraction to gate, so asking would be a request spent on nothing.
  useEffect(() => {
    if (!isDream) return;
    let live = true;
    void fetchEntitlement()
      .then((e) => {
        if (live) setPremium(e.isPremium);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [isDream]);

  // Arriving from a claim on the Self screen ("where did Oreia get that?"): select the quoted
  // passage so the answer is the person's own words, in place, in the entry they wrote.
  //
  // Entries load asynchronously, so this waits for the body and then runs once. The quote — not
  // the stored offsets — decides where the highlight lands, and if the passage has since been
  // edited away we say so and highlight nothing, because marking the wrong sentence in someone's
  // journal is worse than marking none.
  useEffect(() => {
    if (!highlight || highlightDone.current || !body) return;
    highlightDone.current = true;

    const span = resolveCitation(body, highlight);
    if (!span) {
      setHighlightState('drifted');
      return;
    }

    const el = bodyRef.current;
    if (el) {
      el.focus();
      el.setSelectionRange(span.start, span.end);   // browsers scroll the selection into view
    }
    setHighlightState('found');
  }, [highlight, body]);

  const wordCount = useMemo(() => body.trim().split(/\s+/).filter(Boolean).length, [body]);
  const canSave = body.trim().length > 0 && !saving;

  // The symbols a cover is drawn from, parsed the way the save parses them. Split out from the
  // preview below so the picture is recomputed when the symbols change and not when the story does.
  const previewSymbols = useMemo(
    () =>
      symbolsText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [symbolsText],
  );

  // What the column shows when the entry has no photo of its own. Derived from the live composer
  // rather than from `existing`, so a brand-new dream's cover follows the symbols as they are typed
  // instead of waiting for a save. `automaticPreview` is the same picture with the dreamer's cover
  // choice ignored, so the chooser can show Oreia's pick beside the one they chose.
  const previewId = existing?.id ?? UNSAVED_PREVIEW_ID;
  const automaticPreview = useMemo(
    () =>
      entryPreview({
        id: previewId,
        kind: mode,
        symbols: previewSymbols,
      }),
    [previewId, mode, previewSymbols],
  );
  const preview = useMemo(
    () =>
      entryPreview({
        id: previewId,
        kind: mode,
        symbols: previewSymbols,
        coverArt: details.coverArt,
      }),
    [previewId, mode, previewSymbols, details.coverArt],
  );

  /**
   * The pictures as the facing page's column — built once and handed to whichever panel is on
   * screen, since a dream's column lives inside its fields and a waking entry's is the whole panel.
   * Only at the companion width: below it the strip above the writing is still the layout.
   */
  const photoColumn = isWide ? (
    <EntryPhotoColumn
      photos={photos.photos}
      preview={preview}
      onRemove={(id) => void photos.remove(id)}
      onOpen={setViewerIndex}
      onPick={(files) => void photos.add(files)}
      busy={photos.busy}
      onChooseCover={isDream ? () => setShowCover(true) : undefined}
      coverChosen={Boolean(details.coverArt)}
    />
  ) : null;

  /**
   * Everything on screen, as the shape the entries API wants.
   *
   * Shared by autosave and by publishing so a draft is not a lesser copy of an entry — it is the
   * same row, with `isDraft` as the only difference. That is what lets publishing be an update
   * rather than a migration: the id, the photos and the date all stay where they are.
   */
  const buildEntry = useCallback(
    (finalTitle: string, isDraft: boolean): EntryDraft => {
      const structured = isDream && hasStructuredDreamData(details);
      const typed = symbolsText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      return {
        id: entryRowId ?? undefined,
        kind: mode,
        title: finalTitle,
        body: body.trim(),
        // iOS's `resolvedDreamType`: an explicit pick wins, and "Dream" — its default — defers to what
        // the pages imply, so a dream marked Lucid on the Lucidity page is filed as lucid without the
        // writer having to also say so up here.
        dreamType: isDream ? (dreamType !== 'normal' ? dreamType : derivedDreamType(details)) : null,
        // A real synced column iOS reads, with no control on the web any more. Carried over
        // untouched on an edit rather than recomputed: a dream captured in Oblivion on the phone
        // must not be reclassified just because it was opened here. New entries are simply not.
        isOblivion: existing?.isOblivion ?? false,
        // Typed symbols win; otherwise derive them the way iOS does, so the same dream produces the
        // same feed chips whichever device wrote it.
        symbols: typed.length > 0 ? typed : structured ? deriveSymbols(details) : [],
        dreamDetails: structured ? details : null,
        createdAt: entryDate,
        isDraft,
      };
    },
    [entryRowId, mode, isDream, details, symbolsText, body, dreamType, existing, entryDate],
  );

  /**
   * Fold a reading into the entry.
   *
   * This is how an analysis reaches the nightly Archetype portrait: the portrait is built from
   * entries and there is no endpoint for writing memory directly, so the reading has to become part
   * of what was written. iOS's `sendToArchetype`, heading and all, so a dream read on one client
   * leaves the same trace as one read on the other.
   */
  const foldAnalysis = useCallback(
    (heading: string, text: string) => {
      if (!text.trim()) return;
      const next = `${body.trimEnd()}\n\n— ${heading} —\n${text.trim()}`;
      setBody(next);

      // A published entry is deliberately not autosaved — closing the tab must not withdraw it from
      // the journal — so folding into one has to write it down here, or the reading is lost on exit.
      if (isEditing && existing && !existing.isDraft) {
        void save({
          ...buildEntry(title.trim() || existing.title, false),
          body: next,
        }).catch(() => {});
      }
    },
    [body, isEditing, existing, save, buildEntry, title],
  );

  /**
   * Carry a reading into a real conversation, with the reading already in the box.
   *
   * Placed rather than sent, like every other seeded opening in the app: the first thing said to
   * Oreia should still be the person's own choice of words. Anything unsaved here is written to
   * drafts by the exit handler on the way out.
   */
  const discussAnalysis = useCallback(
    (prompt: string) => {
      setAnalysisKind(null);
      navigate('/oreia', { state: { prompt } });
    },
    [navigate],
  );

  /**
   * Read the dream and fill the pages Oreia can, asked for on purpose.
   *
   * Gaps only, and it says what it did rather than leaving the dreamer to diff a form against their
   * memory of it. Errors are reported here rather than in the save banner: this is a thing they
   * pressed, and the answer belongs next to the button.
   */
  const runExtraction = useCallback(async () => {
    if (extracting) return;
    setExtractNote(null);

    if (!longEnoughToExtract(body)) {
      setExtractNote('Write a little more of the dream first.');
      return;
    }

    setExtracting(true);
    try {
      const extracted = await extractDreamDetails(body);
      if (!extracted) {
        setExtractNote('Oreia could not make anything of this one.');
        return;
      }

      let added = false;
      setDetails((current) => {
        const merged = mergeExtracted(current, extracted);
        added = merged !== current;
        return markOreiaFilled(merged);
      });
      setExtractNote(
        added ? 'Filled in what was missing.' : 'Nothing to add — you had it all already.',
      );
    } catch (err) {
      setExtractNote(
        err instanceof ApiError && err.isPaymentRequired
          ? 'Reading the dream for you is part of Oreia Premium.'
          : 'Oreia could not read the dream just now.',
      );
    } finally {
      setExtracting(false);
    }
  }, [body, extracting]);

  /**
   * The silent pass iOS makes once a dream is saved — and only then, and only into a blank form.
   *
   * Fired without being awaited, because the entry is already written and nobody should wait on a
   * model to leave the composer, and it swallows everything it catches: an extraction nobody asked
   * for and was never promised is not worth a banner about the premium gate.
   *
   * It used to run after every save. `mergeExtracted` only appends, so a tag the dreamer had deleted
   * came back the next time they opened the entry. Once Oreia has filled the pages, or the dreamer
   * has typed anything themselves, this must not run again.
   */
  const backfillDetails = useCallback(
    async (id: string, base: EntryDraft) => {
      if (!shouldSilentBackfill(details)) return;

      try {
        const extracted = await extractDreamDetails(body);
        if (!extracted) return;

        const merged = markOreiaFilled(mergeExtracted(details, extracted));
        if (merged === details) return;

        const typed = symbolsText
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        await save({
          ...base,
          id,
          dreamDetails: merged,
          // The chips follow the elements the extraction just added, the same way they would had the
          // dreamer typed them — otherwise a filled-in dream would sit in the feed with no symbols.
          symbols: typed.length > 0 ? typed : deriveSymbols(merged),
        });
      } catch {
        // Premium gate, energy cap, or a dropped connection. The entry is saved either way.
      }
    },
    [body, details, symbolsText, save],
  );

  const onSave = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);

    try {
      // Titles are optional for the writer, not for the feed — ask the model for one rather than
      // filling the journal with "Untitled". Only at this point: a draft is allowed to be nameless,
      // and spending a model call on something still being written would be premature.
      let finalTitle = title.trim();
      if (!finalTitle) {
        finalTitle = (await suggestTitle(body, mode)) ?? defaultTitle(body);
      }

      // Set before the await, not after. Publishing an autosaved draft unmounts this screen, and the
      // exit handler must already know not to save it back as unfinished.
      published.current = true;

      const saved = await save(buildEntry(finalTitle, false));
      // Photos chosen before the first save have been waiting for the entry to exist. Awaited rather
      // than fired off, so navigating away cannot cancel the upload mid-flight and lose them.
      await photos.flush(saved.id);

      // Written from inside a journal, so it is filed there as well as into the feed. Best-effort:
      // the entry is saved either way, and refusing to publish someone's writing because a
      // membership row failed would be the wrong thing to protect.
      if (destination) {
        await addToJournal(destination.id, saved.id).catch(() => {});
      }

      // Read the dream and fill in what was left blank — once, and only if the pages are still
      // empty. A later save of the same dream must not append tags the dreamer already edited.
      if (isDream && premium && shouldSilentBackfill(details)) {
        void backfillDetails(saved.id, buildEntry(finalTitle, false));
      }

      navigate(closeTo, { replace: true });
    } catch (err) {
      published.current = false;
      setError(err instanceof Error ? err.message : 'Could not save your entry.');
      setSaving(false);
    }
  }, [
    canSave,
    title,
    body,
    mode,
    buildEntry,
    save,
    photos,
    navigate,
    closeTo,
    destination,
    addToJournal,
    isDream,
    premium,
    backfillDetails,
  ]);

  /**
   * Write the current state down as an unfinished entry.
   *
   * Never for an entry that is already in the journal: editing a published entry and closing the tab
   * must not withdraw it from the journal, so only a new entry or an existing draft is autosaved.
   * Errors are swallowed on purpose — this happens while someone is mid-sentence, and interrupting
   * them with a banner about a background save they never asked for would be worse than the risk it
   * is protecting against.
   */
  const savedSignature = useRef<string | null>(null);
  const saveDraft = useCallback(async () => {
    if (published.current) return;
    if (isEditing && !existing?.isDraft) return;
    if (body.trim().length < DRAFT_MIN_CHARS && !title.trim()) return;

    // Saving the first draft changes this component's own state, which would otherwise schedule an
    // identical second save. Comparing what is about to be written against what already was keeps
    // idle time genuinely idle.
    const draft = buildEntry(title.trim(), true);
    const signature = JSON.stringify([draft.title, draft.body, draft.kind, draft.dreamType, draft.symbols, draft.dreamDetails]);
    if (signature === savedSignature.current) return;

    try {
      const saved = await save(draft);
      savedSignature.current = signature;
      setDraftId(saved.id);
      setDraftSavedAt(new Date());
    } catch {
      // Locked vault or a dropped connection. The text is still on screen, and the next pause — or
      // the Save button — tries again.
    }
  }, [isEditing, existing, body, title, save, buildEntry]);

  // The pause after typing. Every dependency of `buildEntry` is a dependency here too, so adding a
  // symbol or filling in a dream page is saved the same way a sentence is.
  useEffect(() => {
    const timer = setTimeout(() => void saveDraft(), DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [saveDraft]);

  /**
   * Leaving with something unfinished on screen.
   *
   * Held in a ref and fired from an unmount effect that runs exactly once, because by the time the
   * cleanup runs the component is already going and a stale closure would save whatever the state
   * looked like on first render. Not awaited — `save` reaches the network on its own, and blocking
   * navigation on it would make closing the composer feel broken.
   */
  const saveDraftOnExit = useRef(saveDraft);
  saveDraftOnExit.current = saveDraft;
  useEffect(() => () => void saveDraftOnExit.current(), []);

  // Cmd/Ctrl+Enter saves, Escape closes — table stakes for anything people type into on a laptop.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        void onSave();
      }
      if (event.key === 'Escape') navigate(closeTo);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onSave, navigate, closeTo]);

  const now = entryDate;

  // One control, handed to whichever presentation of the Symbols fields is on screen: the tabbed
  // page below the companion width, the facing panel above it.
  const extractSlot = isDream ? (
    <ExtractFieldsButton busy={extracting} note={extractNote} onRun={() => void runExtraction()} />
  ) : null;

  return (
    <div className="osheet-screen ocomposer">
      <div className="bg">
        <div style={{ position: 'absolute', inset: 0, background: 'var(--bg-bloom-1), var(--bg-bloom-2)' }} />
      </div>

      <div className="osheet-top">
        <button className="osheet-x" onClick={() => navigate(closeTo)} aria-label="Close">
          <IconClose />
        </button>

        <div className="omode" role="group" aria-label="Entry kind">
          <button
            className={`waking${mode === 'waking' ? ' on waking' : ''}`}
            onClick={() => {
              setMode('waking');
              // The dream pages are gone in waking mode; leaving the selection behind would show a
              // blank card on the next switch back to Dream.
              setPage('story');
            }}
            aria-pressed={mode === 'waking'}
          >
            Waking
          </button>
          <button
            className={`dream${mode === 'dream' ? ' on dream' : ''}`}
            onClick={() => setMode('dream')}
            aria-pressed={mode === 'dream'}
          >
            Dream
          </button>
        </div>

        <button className="osheet-done" onClick={onSave} disabled={!canSave}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div
        className={`osheet-card ${isDream ? 'weave' : 'light'}${
          isTinted(reader, mode) ? ' reader-tinted' : ''
        }`}
        style={readerVars(reader, mode)}
      >
        {/* The title took the weekday's place at the top of the page, and the date moved down to
            say the whole thing — an entry now has a name of its own instead of borrowing the day's.
            An input rather than a heading you click to edit: the field is always ready, so naming
            an entry costs a tap on the words rather than a tap on something that then becomes a
            field. Left blank, Oreia still suggests one on save, as it always has. */}
        <div className="datehd">
          {/* A textarea rather than an input, for one reason: a title long enough to matter is
              longer than the card is wide, and an input would hide the end of it behind the edge —
              you would be naming something you cannot read. This wraps to a second line instead.
              Enter still means "done naming it" and moves to the page, as it would in a form. */}
          <textarea
            ref={titleRef}
            className="dow otitle"
            value={title}
            onChange={(e) => setTitle(e.target.value.replace(/\s*\n\s*/g, ' '))}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              bodyRef.current?.focus();
            }}
            placeholder="Title"
            aria-label="Title"
            maxLength={120}
            rows={1}
            spellCheck
          />
          <span className="dfull">{longDate(now)}</span>
          <span className="drule" />
        </div>

        {/* Where this is going, said before it is written rather than after. "Write here" is the
            one path where saving files the entry somewhere the writer chose, and a promise like
            that has to be visible while they are deciding whether to keep typing. */}
        {destination ? (
          <span className="osheet-in">
            <IconFolder /> Saving to <em>{destination.title || 'this journal'}</em>
          </span>
        ) : null}

        {isDream && !hasCompanion ? (
          <div style={{ margin: '2px 0 16px' }}>
            <DreamPageTabs page={page} onChange={setPage} />
          </div>
        ) : null}

        {activePage === 'symbols' ? (
          <DreamSymbolsPage details={details} onChange={patchDetails} extract={extractSlot} />
        ) : null}
        {activePage === 'details' ? (
          <DreamDetailsPage details={details} onChange={patchDetails} />
        ) : null}
        {activePage === 'lucidity' ? (
          <DreamLucidityPage details={details} onChange={patchDetails} />
        ) : null}

        {activePage === 'story' ? (
          <>
            {/* Above the writing, as on iOS, where photos sit in a banner at the top of the card.
                Only while there is no facing page to put them in: at the companion width they move
                into the column, and rendering both would show the same pictures twice. */}
            {!isWide ? (
              <EntryPhotoStrip
                photos={photos.photos}
                onRemove={(id) => void photos.remove(id)}
                onOpen={setViewerIndex}
              />
            ) : null}

            {!isWide && isDream && photos.photos.length === 0 ? (
              <button
                className="ophoto-cover ophoto-cover-inline"
                onClick={() => setShowCover(true)}
                aria-label="Choose a picture"
              >
                {preview.kind === 'gradient' ? (
                  <span className="ophoto-art" style={{ background: preview.background }} />
                ) : (
                  <img
                    className="ophoto-art"
                    src={preview.url}
                    alt={preview.label}
                    draggable={false}
                  />
                )}
              </button>
            ) : null}

            {highlightState !== 'idle' ? (
              <div className={`ocite-banner${highlightState === 'drifted' ? ' drifted' : ''}`} role="status">
                {highlightState === 'found'
                  ? 'Oreia quoted the highlighted words from this entry.'
                  : 'Oreia quoted this entry, but that passage has since been edited.'}
              </div>
            ) : null}

            <div className="prompt">{PROMPTS[mode]}…</div>

            <textarea
              ref={bodyRef}
              className="owrite"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={
                isDream
                  ? 'of a folding staircase that kept rebuilding itself…'
                  : 'a quiet hour at my window…'
              }
              aria-label="Your entry"
              spellCheck
            />


            {draftSavedAt ? (
              <p className="omono" style={{ marginTop: 10 }}>
                {isEditing ? 'kept in drafts' : 'saved to drafts — Save when it’s finished'}
              </p>
            ) : null}
          </>
        ) : null}

        {/* Errors belong to the save, not to a page, so they stay visible wherever you are. */}
        {error ? (
          <div style={{ marginTop: 12 }}>
            <Notice tone="danger">{error}</Notice>
          </div>
        ) : null}

        {photos.error ? (
          <div style={{ marginTop: 12 }}>
            <Notice tone="info">{photos.error}</Notice>
          </div>
        ) : null}

        <PhotoViewer
          photos={photos.photos}
          index={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onCrop={(id, crop) => void photos.setCrop(id, crop)}
        />

        <ReaderSettingsSheet
          open={showMeta}
          settings={reader}
          kind={mode}
          onChange={updateReader}
          onClose={() => setShowMeta(false)}
        >
          <div className="ostack" style={{ gap: 14 }}>
            {/* The title used to live here, behind the sliders button, which meant most entries
                never got one. It is at the top of the page now. */}
            {isDream ? (
              <div className="ostack" style={{ gap: 7 }}>
                <span className="olabel">Kind of dream</span>
                <div className="ofilters" style={{ margin: 0 }}>
                  {DREAM_TYPES.map(({ value, label }) => (
                    <button
                      key={value}
                      className={`ochip${dreamType === value ? ' active' : ''}`}
                      onClick={() => setDreamType(value)}
                      aria-pressed={dreamType === value}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="ostack" style={{ gap: 7 }}>
              <label className="olabel" htmlFor="composer-symbols">
                Symbols
              </label>
              <input
                id="composer-symbols"
                className="oinput"
                value={symbolsText}
                onChange={(e) => setSymbolsText(e.target.value)}
                placeholder="stairs, ocean, keys"
              />
              <span className="otext-mute" style={{ fontSize: 12.5 }}>
                Comma separated. Oreia also finds these on its own — this is for the ones that matter
                to you.
              </span>
            </div>

            <div className="ostack" style={{ gap: 7 }}>
              <label className="olabel" htmlFor="composer-date">
                Date &amp; time
              </label>
              <input
                id="composer-date"
                className="oinput"
                type="datetime-local"
                value={toLocalInput(entryDate)}
                // Capped at the present, as iOS caps its date picker: you cannot journal into
                // the future.
                max={toLocalInput(new Date())}
                onChange={(e) => {
                  const next = new Date(e.target.value);
                  if (!Number.isNaN(next.getTime())) setEntryDate(next);
                }}
              />
              <span className="otext-mute" style={{ fontSize: 12.5 }}>
                For something you are writing down later than it happened.
              </span>
            </div>

            {isEditing && existing ? (
              <button
                className="olink-plain"
                style={{ color: 'var(--danger)', fontSize: 13.5 }}
                onClick={() =>
                  setConfirm({
                    title: existing.isDraft ? 'Delete this draft?' : 'Delete this entry?',
                    body: (
                      <>
                        <strong>{title.trim() || longDate(existing.createdAt)}</strong> and anything
                        Oreia noticed in it are removed for good. This cannot be undone.
                      </>
                    ),
                    confirmLabel: existing.isDraft ? 'Delete draft' : 'Delete entry',
                    danger: true,
                    onConfirm: async () => {
                      await remove(existing.id);
                      navigate('/journal', { replace: true });
                    },
                  })
                }
              >
                Delete this entry
              </button>
            ) : null}
          </div>
        </ReaderSettingsSheet>

        {showScan&&<PageScanSheet onClose={()=>setShowScan(false)} onInsert={text=>setBody(b=>[b,text].filter(Boolean).join('\n\n'))}/>}
        <VoiceDictationSheet
          open={showVoice}
          onClose={() => setShowVoice(false)}
          onInsert={(text) =>
            // Appended rather than inserted at the caret: the textarea has lost focus to the sheet by
            // now, so a remembered caret position is as likely to be stale as right.
            setBody((current) => (current.trim() ? `${current.trimEnd()} ${text}` : text))
          }
        />

        <div className="osheet-actions">
          <button className="oact" aria-label="Go deeper with Oreia" title="Go deeper with Oreia" onClick={()=>navigate('/write/conversation',{state:{seed:{kind:mode,body:existing?undefined:body},sourceEntryId:existing?.id,guide:mode==='dream'?'What part of this dream would you like to explore?':'What would you like to understand about this entry?'}})}><IconSpark/></button>
          <button
            className="oact"
            aria-label="Reader settings and entry options"
            aria-expanded={showMeta}
            onClick={() => setShowMeta((v) => !v)}
          >
            <IconSliders />
          </button>
          {/* Hidden where it cannot work — Firefox has no Web Speech API, and a dead button is a
              worse answer than no button. */}
          {canDictate() ? (
            <button className="oact" aria-label="Dictate with your voice" onClick={() => setShowVoice(true)}>
              <IconMic />
            </button>
          ) : null}
          <button className="oact" aria-label="Import a written page" onClick={()=>setShowScan(true)}><IconImage /></button>
          <AnalysisMenuButton
            mode={mode}
            disabled={body.trim().length === 0}
            onPick={setAnalysisKind}
          />
          {/* The pictures have a facing page of their own at the companion width, so the toolbar
              gives that slot to the sparkle there. Below it a dream opens the cover chooser —
              gallery, upload, Imagine — and a waking entry still attaches a photo directly. */}
          {!isWide ? (
            isDream ? (
              <button className="oact" aria-label="Choose a picture" onClick={() => setShowCover(true)}>
                <IconImage />
              </button>
            ) : (
              <PhotoPickerButton onPick={(files) => void photos.add(files)} busy={photos.busy} />
            )
          ) : null}
          <span className="acount">
            {wordCount} {wordCount === 1 ? 'word' : 'words'}
            {draftSavedAt ? ' · draft saved' : ''}
          </span>
        </div>
      </div>

      {/* After the card in the DOM as well as beside it on screen, so Tab goes story then column.
          A dream's pictures sit at the top of its fields; a waking entry has no fields, so its
          panel is the pictures and nothing else — one facing page either way, never two.

          Each takes the card's reading surface as well as its fill: the panel is meant to read as
          the facing page of one opened object, and a chosen background that stopped at the gutter
          would make that claim false in four of the five settings. */}
      {hasCompanion ? (
        <DreamCompanionPanel
          details={details}
          onChange={patchDetails}
          onImagine={() => setShowImagine(true)}
          premium={premium}
          photos={photoColumn}
          extract={extractSlot}
          tinted={isTinted(reader, mode)}
          suppressed={analysisKind != null}
          style={readerVars(reader, mode)}
        />
      ) : isWide ? (
        <CompanionPanel
          id="composer-photos"
          title="Photos"
          storageKey={PHOTO_PANEL_KEY}
          showLabel="Show photos"
          hideLabel="Hide photos"
          surface="light"
          tinted={isTinted(reader, mode)}
          suppressed={analysisKind != null}
          style={readerVars(reader, mode)}
        >
          {photoColumn}
        </CompanionPanel>
      ) : null}

      {/* Portalled to body from inside the component; kept next to the other overlays so a reading
          is of the entry as it stands, including the structured pages. Folding is dreams only. */}
      <EntryAnalysisSheet
        kind={analysisKind}
        body={body}
        details={details}
        extraSymbols={previewSymbols}
        onClose={() => setAnalysisKind(null)}
        onDiscuss={discussAnalysis}
        onFold={isDream ? foldAnalysis : undefined}
      />

      <CoverChooser
        open={showCover}
        onClose={() => setShowCover(false)}
        automatic={automaticPreview}
        chosen={details.coverArt}
        symbols={[...details.elements, ...details.setting, ...details.characters, ...previewSymbols]}
        onPick={(stem) =>
          patchDetails(stem ? { coverArt: stem } : { coverArt: undefined })
        }
        onUpload={(files) => void photos.add(files)}
        onImagine={
          isDream
            ? () => {
                setShowCover(false);
                setShowImagine(true);
              }
            : undefined
        }
        busy={photos.busy}
      />

      {/* Imagine sends the dream's prose, not its structured fields — the same text iOS sends, and the
          same text the Worker de-identifies before any of it reaches an image provider. An unsaved
          entry has no id yet, and the flow says so rather than silently failing to attach. */}
      <DreamVisionFlow
        open={showImagine}
        onClose={() => setShowImagine(false)}
        entryId={existing?.id ?? null}
        dreamText={body}
        onAttached={() => photos.reload()}
      />

      {/* Outside the options sheet that opens it: the sheet is a stacking context of its own, and a
          confirmation that can be scrolled out of view is not one. */}
      <ConfirmDialog spec={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

/**
 * "Let Oreia fill these in", at the top of the Symbols fields.
 *
 * Placed where the work it saves is, rather than in the toolbar: the dreamer is looking at a column
 * of empty fields at the moment this becomes worth pressing. It reports what it did underneath
 * itself — an extraction that says nothing is indistinguishable from one that failed.
 */
function ExtractFieldsButton({
  busy,
  note,
  onRun,
}: {
  busy: boolean;
  note: string | null;
  onRun(): void;
}) {
  return (
    <div className="oextract">
      <button className="oextract-btn" onClick={onRun} disabled={busy} aria-busy={busy}>
        <IconSpark />
        <span>{busy ? 'Reading your dream…' : 'Let Oreia fill these in'}</span>
      </button>
      {note ? (
        <p className="oextract-note" role="status">
          {note}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Format for a `datetime-local` input, which takes local wall-clock time with no zone. Going through
 * toISOString would shift the value by the reader's offset and quietly relabel a late-night entry as
 * the next morning.
 */
function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** A readable fallback when the title model is unreachable — better than "Untitled". */
function defaultTitle(body: string): string {
  const firstLine = body.trim().split(/\n/)[0] ?? '';
  const words = firstLine.split(/\s+/).filter(Boolean).slice(0, 6).join(' ');
  return words.length > 0 ? words.replace(/[.,;:!?]+$/, '') : 'Untitled';
}
